import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useDietSupplements } from "../DietSupplementsContext";
import type { DietDetail } from "../DietSupplementsContext";
import type { DietSupplementsStackParamList } from "../DietSupplementsNavigator";
import { DIET_OPTIONS } from "../options";
import type { DietType } from "../options";

export function DietScreen() {
  const t = useTranslations();
  const { state, updateState } = useDietSupplements();
  const navigation =
    useNavigation<NavigationProp<DietSupplementsStackParamList>>();

  const selectedDiets = useMemo(
    () => new Set(state.diets.map((d) => d.diet)),
    [state.diets],
  );

  const toggleDiet = (diet: DietType) => {
    if (selectedDiets.has(diet)) {
      // Remove
      const toRemove = state.diets.find((d) => d.diet === diet);
      const idsToDelete = toRemove?.id
        ? [...state.dietIdsToDelete, toRemove.id]
        : state.dietIdsToDelete;
      const remaining = state.diets.filter((d) => d.diet !== diet);
      updateState({
        hasDiets: remaining.length > 0 ? true : null,
        diets: remaining,
        dietIdsToDelete: idsToDelete,
      });
    } else {
      // Add
      const newDiet: DietDetail = { diet };
      updateState({
        hasDiets: true,
        diets: [...state.diets, newDiet],
      });
    }
  };

  const handleSave = () => {
    navigation.navigate("SupplementsIntro");
  };

  const isValid = state.diets.length > 0;

  return (
    <QuestionLayout
      title={t("med_history.lifestyle.diet_supplements.diet_title")}
      subtitle={t("med_history.lifestyle.diet_supplements.diet_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={!isValid}
      saveLabel={t("med_history.lifestyle.next")}
    >
      <View style={styles.container}>
        <View style={styles.grid}>
          {DIET_OPTIONS.map((option) => (
            <View key={option.id} style={styles.cell}>
              <SquareOptionButton
                icon={option.icon}
                label={t(option.labelKey)}
                isSelected={selectedDiets.has(option.id)}
                onPress={() => toggleDiet(option.id)}
                labelSize="sm"
              />
            </View>
          ))}
        </View>
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    width: "100%",
  },
  cell: {
    width: "47%",
  },
});
